import { ROUTES } from '@/lib/constants/routes';
import type { DataRoomService } from '@/lib/types/data-room';
import type { RefusalReason } from './api/data-room.api';

/**
 * How the data room is spoken about. Premium (and the post-MVP subscription,
 * its paid continuation) is the SR NEXT "Legal Command Center"; an Issue
 * Report is the reporting view only (client copy, 2026-10-09).
 */
export type DataRoomVoice = 'command_center' | 'issue_report';

/** The voice for each service, resolved once. */
export const DATA_ROOM_VOICE: Record<DataRoomService, DataRoomVoice> = {
  premium: 'command_center',
  subscription: 'command_center',
  issue_report: 'issue_report',
};

/** One dashboard card's copy and destination. */
interface DashboardCardCopy {
  title: string;
  body: string;
  action: string;
  to: string;
}

/**
 * Query parameters the backend appends to the return URL after a refused
 * handoff. WIRE CONTRACT with `Backend/src/modules/daxe/daxe.constants.ts`
 * (`DAXE_RETURN_*_PARAM`): the backend redirects a browser here, so these are
 * read from the URL rather than passed in router state, which a fresh
 * top-level navigation does not carry.
 *
 * `outcome` is `reissue` (relaunch silently, enter no error state) or `error`
 * (render `reason`'s state). `startupId` is present on every refusal the
 * Platform itself generated, and absent on DAXE's own error return - their
 * callback names no startup - which is why a reissue without it can only
 * offer a way back rather than relaunching.
 */
export const DATA_ROOM_RETURN_PARAMS = {
  OUTCOME: 'outcome',
  REASON: 'reason',
  STARTUP_ID: 'startupId',
} as const;

/** Copy and the way out for one refused launch. */
interface RefusalCopy {
  title: string;
  body: string;
  /** Label of the primary action. */
  action: string;
  /**
   * Where the primary action goes. `null` means the action is a retry rather
   * than a destination - only `upstream_unavailable` is retryable, because it
   * is the only reason that resolves without anyone changing anything.
   */
  actionTo: string | null;
}

/**
 * Data-room feature copy (MYN-89 / MYN-164). Feature-local, same as
 * `features/startups/constants.ts` - `ui-strings.ts` is already past its own
 * LOC cap and this vocabulary belongs to exactly one feature.
 */
export const DATA_ROOM_STRINGS = {
  LAUNCH: {
    OPEN: 'Open AI Data Room',
  },
  /**
   * The dashboard's top-right card (client copy, 2026-10-09), by voice: the
   * Legal Command Center for SR NEXT Premium, the Issue Report card for every
   * other startup, bought or not. Both lead to the Data Room page, which shows
   * the report, or explains it and how to get it, from the startup's access.
   */
  DASHBOARD_CARD: {
    command_center: {
      title: 'Access Your Legal Command Center',
      body: 'Open your AI Data Room, NEXT Investor Connect Workspace, and investor readiness tools',
      action: 'Open Command Center',
      to: ROUTES.DATA_ROOM,
    },
    issue_report: {
      title: 'What Could Stall Your Raise?',
      body: 'Get your Investor Readiness Issue Report to uncover potential legal gaps before investors do.',
      action: 'Explore the Issue Report',
      to: ROUTES.DATA_ROOM,
    },
  } satisfies Record<DataRoomVoice, DashboardCardCopy>,
  /**
   * The Issue Report card's action for someone who can buy and has no access
   * yet: straight to the purchase page (UX round 2, MYN-284). The navbar CTA
   * is the "explain first" path to the Data Room page.
   */
  DASHBOARD_CARD_PURCHASE: {
    action: 'Get an Issue Report',
    to: ROUTES.BILLING_PURCHASE,
  },
  /** The Data Room page (`/data-room`), by voice. */
  PAGE: {
    // FR-299 (MYN-331): one page for every logged-in user, named for what it holds.
    TITLE: 'Legal Command Center',
    LOADING: 'Loading your data room',
    STATUS_ACTIVE: 'Access active',
    ENDS: 'Access ends {date}',
    /** The badge once access is inside the 30-day renewal window (A-326). */
    ENDS_IN: (days: number) =>
      days <= 0 ? 'Ends today' : days === 1 ? 'Ends in 1 day' : `Ends in ${days} days`,
    command_center: {
      eyebrow: 'SR NEXT Due Diligence',
      title: 'Your Legal Command Center',
      body: 'Open your AI Data Room, NEXT Investor Connect Workspace, and investor readiness tools.',
    },
    issue_report: {
      eyebrow: 'Investor Readiness Issue Report',
      title: 'Your Issue Report',
      body: 'Your Investor Readiness Issue Report is in the AI Data Room. It shows the legal gaps investors could find, so you can close them first.',
    },
    /**
     * The startup has no access (`no_active_service`): what the Issue Report
     * is and how to get it, from the client's Oct 1 2026 service notes. The
     * public Due Diligence page is separate marketing (MYN-210).
     */
    EXPLAINER: {
      eyebrow: 'Investor Readiness Issue Report',
      title: 'What Could Stall Your Raise?',
      body: 'Get your Investor Readiness Issue Report to uncover potential legal gaps before investors do.',
      ENDED: 'Your last data room access ended on {date}.',
      BUY: 'Get your Issue Report',
      ASK_ADMIN: 'Only a Startup Admin can buy the Issue Report for your startup.',
      HELD: "Your startup's Issue Report is open to the Startup Admin who bought it. It cannot be shared with teammates.",
      SEE_ADMINS: 'See your Startup Admins',
    },
    SEATS_TITLE: 'Data room seats',
    SEATS_USED: '{used} of {limit} seats used',
    SEAT_EMPTY: 'Empty seat',
    MANAGE_SEATS: 'Manage seats in Team Access',
  },
  /**
   * The Issue Report CTA journey (`/issue-report/start`, FR-204, FR-205). One
   * state per step the resolver can stop on; the redirecting steps have none.
   */
  JOURNEY: {
    /** The journey page's title (FR-226). */
    TITLE: 'Your Issue Report',
    LOADING: 'Checking your account',
    COMING_SOON: {
      title: 'The Issue Report is coming soon',
      body: 'It is not on sale yet. Please check back soon.',
    },
    ADMIN_ONLY: {
      title: 'Only a Startup Admin can buy the Issue Report',
      body: 'Ask a Startup Admin at your startup to buy it for the team.',
    },
    OFFER_ERROR: {
      title: 'We could not check the Issue Report right now',
      body: 'Nothing has been charged. Please try again in a moment.',
      action: 'Try again',
    },
    BACK_TO_DUE_DILIGENCE: 'Back to Due Diligence page',
    BACK_TO_DASHBOARD: 'Back to Dashboard',
  },
  HANDOFF: {
    TITLE: 'Opening your data room',
    BODY: 'Taking you to your data room. This only takes a moment.',
  },
  /** Low-key way out, offered alongside every refusal's primary action. */
  BACK_TO_DASHBOARD: 'Back to Dashboard',
  /** A view-as tab (MYN-334/335): the data room is never opened as someone else (§51). */
  VIEW_AS: {
    TITLE: 'Data room hidden in View as user',
    BODY: 'View as user shows this page without the data room, so their documents stay private. Everything else on the page is what they see.',
  },
  /**
   * One state per typed reason (`docs/specs/features/data-room/02-design.md` §12). Every reason names what
   * is wrong AND who can fix it: a refusal with no way forward is the failure
   * mode this table exists to avoid.
   *
   * Typed as a `Record` of the API's own union so adding a reason to the
   * contract is a compile error here until its copy is written.
   *
   * `workspace_suspended` addresses the STARTUP, not the person. Suspension is
   * workspace-level - every member loses access at once - and wording that
   * implies a personal access problem sends members to support over a billing
   * issue only their admin can resolve.
   *
   * Where these actions point: for a MEMBER, the billing-shaped reasons route
   * to Team Access, which is the page that names their Startup Admins. That
   * was originally because no billing UI existed at all; M8 built it, but the
   * member's destination is unchanged on purpose - a member cannot pay, so
   * sending them to a billing page they can only read is a worse dead end than
   * a named person to ask. The Startup Admin's destination DOES change, in
   * `REFUSAL_ADMIN_OVERRIDES` below.
   */
  REFUSAL: {
    no_active_service: {
      title: 'Your startup has no active data room access',
      body: 'The data room opens while your startup has an active Issue Report or Premium access. A Startup Admin can buy an Issue Report from Billing to open it for the team.',
      action: 'See your Startup Admins',
      actionTo: ROUTES.STARTUP_TEAM,
    },
    seat_not_assigned: {
      title: 'You do not have a data room seat',
      body: 'Data room access is granted per person. A Startup Admin has to assign you a seat on the Team Access page before you can open the data room.',
      action: 'Go to Team Access',
      actionTo: ROUTES.STARTUP_TEAM,
    },
    company_email_required: {
      title: 'A verified company email is required',
      body: "The data room can only be opened from a verified email address on your startup's company domain. Your account email is either not verified yet or not on that domain.",
      action: 'Review your account email',
      actionTo: ROUTES.ACCOUNT,
    },
    workspace_suspended: {
      title: "This startup's workspace is suspended",
      body: 'While the workspace is suspended, nobody at this startup can open the data room. Nothing has been deleted - access returns once a Startup Admin clears the outstanding billing or administrative issue.',
      action: 'See your Startup Admins',
      actionTo: ROUTES.STARTUP_TEAM,
    },
    // v1.2 (FR-143). Neither retries: a new attempt would carry the same ended
    // period or meet the same full seats.
    entitlement_expired: {
      title: 'Your data room access period has ended',
      body: "Your startup's access period has ended, so the data room cannot be opened. Your documents are kept. A Startup Admin can start a new access period from Billing.",
      action: 'See your Startup Admins',
      actionTo: ROUTES.STARTUP_TEAM,
    },
    seat_limit_reached: {
      title: 'All data room seats are in use',
      body: "Every data room seat for your startup is taken. Contact your Startup Admin to be given a seat.",
      action: 'See your Startup Admins',
      actionTo: ROUTES.STARTUP_TEAM,
    },
    upstream_unavailable: {
      title: 'The data room is temporarily unavailable',
      body: 'We could not reach the data room just now. This is not a problem with your access or your subscription - try again in a moment.',
      action: 'Try again',
      actionTo: null,
    },
  } satisfies Record<RefusalReason, RefusalCopy>,

  /**
   * What changes for a Startup Admin, and only for the reasons they can
   * actually act on (`02-design.md` §14).
   *
   * An admin looking at "no active access" or "access ended" is the one
   * person who can fix it (by buying an Issue Report), so pointing them at a
   * page naming themselves is absurd. The other reasons are unchanged for
   * everyone: a seat is granted on Team
   * Access, a company email is fixed in the account, and an upstream outage is
   * nobody's to fix.
   *
   * A partial map rather than a second full copy table, so a new reason picks
   * up the shared copy automatically and only an explicit entry diverges.
   */
  REFUSAL_ADMIN_OVERRIDES: {
    no_active_service: { action: 'Go to Billing', actionTo: ROUTES.BILLING },
    workspace_suspended: { action: 'Go to Billing', actionTo: ROUTES.BILLING },
    entitlement_expired: { action: 'Go to Billing', actionTo: ROUTES.BILLING },
  } satisfies Partial<Record<RefusalReason, Pick<RefusalCopy, 'action' | 'actionTo'>>>,
} as const;

/**
 * The Legal Command Center's sections (MYN-331, FR-300): read from the public
 * Due Diligence page's own blocks, so one CMS edit updates both pages, and
 * laid out as in-app cards rather than that page's full-width bands. Hero,
 * stats and quotes are left out.
 */
export const LEGAL_COMMAND_CENTER_PAGE_SLUG = 'due-diligence-platform';
export const LEGAL_COMMAND_CENTER_SECTIONS = {
  ISSUE_REPORT: 'issue-report',
  SEED_PACKAGE: 'seed-financing-package',
  WORKSPACES: 'exclusive-workspaces',
  COMPARE: 'choose-your-next-step',
} as const;

/** Access ending within this many days shows as ending soon: the renewal window (A-326). */
export const ENDING_SOON_DAYS = 30;

/** Copy for the services under the top panel; the services' own text comes from the CMS. */
export const COMMAND_CENTER_SERVICES = {
  HEADING: 'Legal services',
  SHOW_ALL: (count: number) => `See all ${count} inclusions`,
  SHOW_FEWER: 'Show fewer',
  OPEN_DATA_ROOM: 'Open data room',
  OPEN_ISSUE_REPORT: 'Open Issue Report',
  INCLUDED: 'Included in your access',
  CURRENT_PACKAGE: 'Your current package',
  INCLUDED_ON_RENEW: 'Included when you renew',
  PREVIOUS_PACKAGE: 'Your previous package',
  REOPENS_ON_RENEW: 'Reopens when you renew',
  WITH_PACKAGE: 'Included with the Seed Financing Package',
  YOU_HAVE_THIS: 'You have this',
  ASK_ADMIN: 'Ask your Startup Admin to buy',
  NEW_TAB: '(opens in a new tab)',
} as const;

/** The buy prompt for someone with no startup yet (FR-299, A-329): the public page's own CTA. */
export const LEGAL_COMMAND_CENTER_BUY = {
  EYEBROW: 'INVESTOR READINESS ISSUE REPORT',
  TITLE: 'Find the gaps before investors do',
  BODY: 'Get an AI-generated Issue Report on your legal documents, with a NEXT attorney review session. We set up your startup first if you do not have one yet.',
  CTA: 'Get an Issue Report',
} as const;

